import { useEffect, useCallback } from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'

import { SVGWrapper } from '../../components'
import { SendArrow } from '../../assets/icons'
import ChatDisclaimer from './ChatDisclaimer'
import {
  Footer,
  FooterInner,
  InputShell,
  InputInner,
  TextArea,
  SendButton,
  CHAT_MOBILE,
} from './styled'

const ChatInput = ({
  store,
  chat: chatProp,
  text,
  setText,
  inputRef,
  compact,
  autoFocus = true,
  onSubmit,
}) => {
  const isMobile = useMediaQuery(CHAT_MOBILE.replace('@media ', ''))
  // See ChatView: a caller owning a specific chat passes it, so the placeholder
  // can't reflect a conversation this input isn't attached to.
  const chat = chatProp === undefined ? store.activeChat : chatProp
  const loading = store.responseLoading
  const chatId = chat?.id ?? null
  // Compact (SBSA panel) has no room for a labelled Send button, and mobile
  // already collapses to the icon-only circle.
  const iconOnly = isMobile || compact

  const placeholder = chat?.hasPreviousHumanMessages ? 'Continue chatting' : 'Ask anything'

  // Grow the textarea with its content up to the CSS max-height, then scroll.
  const resize = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [inputRef])

  useEffect(() => {
    resize()
  }, [text, resize])

  // Place the cursor in the field on entry and whenever the active chat changes
  // (including the chat opened by default when entering Hey Nic), on mobile too:
  // the shell now sizes itself to the space above the keyboard, so the greeting
  // and starter pills stay visible and the user can type straight away. The SBSA
  // panel opts out (`autoFocus={false}`) — it is a side panel beside the step
  // content, so pulling focus into it on arrival would fight the page.
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [chatId, inputRef, autoFocus])

  const submit = () => {
    const trimmed = text.trim()
    if (!trimmed || loading) return
    // The SBSA panel routes sends through the store's per-step action, which
    // creates that step's chat on the first interaction — `sendMessage` alone
    // needs an already-open chat and would silently no-op.
    if (onSubmit) onSubmit(trimmed)
    else store.sendMessage(trimmed)
    setText('')
  }

  const handleKeyDown = e => {
    // Enter submits; Shift+Enter inserts a newline. Ignored while loading.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  return (
    <Footer compact={compact}>
      <FooterInner compact={compact}>
        {/* Typing stays enabled while streaming; only submission is blocked. */}
        <InputShell compact={compact}>
          <InputInner compact={compact}>
            <TextArea
              ref={inputRef}
              rows={1}
              compact={compact}
              value={text}
              placeholder={placeholder}
              onChange={e => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-label='Message Hey Nic'
            />
            <SendButton
              type='button'
              compact={compact}
              onClick={submit}
              disabled={loading || !text.trim()}
              aria-label='Send'>
              {iconOnly ? (
                <SVGWrapper svg={SendArrow} size={compact ? 'mediumLarge' : 'large'} />
              ) : (
                <>
                  Send
                  <SVGWrapper svg={SendArrow} size='large' />
                </>
              )}
            </SendButton>
          </InputInner>
        </InputShell>
        <ChatDisclaimer />
      </FooterInner>
    </Footer>
  )
}

ChatInput.propTypes = {
  /** The chat this input sends to. Defaults to the store's shared `activeChat`. */
  chat: PropTypes.object,
  text: PropTypes.string.isRequired,
  setText: PropTypes.func.isRequired,
  inputRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  /** Narrow-container sizing for the SBSA panel (pill field, round send icon). */
  compact: PropTypes.bool,
  /** Focus the field on mount / chat change. Defaults to true. */
  autoFocus: PropTypes.bool,
  /** Send handler, overriding the default `store.sendMessage`. */
  onSubmit: PropTypes.func,
}

export default inject('store')(observer(ChatInput))
